import { useState, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, MapPin, Video, Phone } from "lucide-react";
import { Navbar } from "../components/site/Navbar";
import { Footer } from "../components/site/Footer";
import { AppointmentDialog } from "../components/site/AppointmentDialog";
import { CookieBanner } from "../components/site/CookieBanner";
import { usePageMeta } from "../hooks/usePageMeta";
import { useJsonLd } from "../hooks/useJsonLd";

const REGIONS = {
  "new-hampshire": {
    code: "NH",
    country: "US",
    region: "New Hampshire",
    regionShort: "NH",
    headline: "Adult Speech-Language Pathologist serving New Hampshire.",
    eyebrow: "New Hampshire · Licensed SLP",
    intro:
      "Evidence-based adult speech therapy for Parkinson's disease, post-stroke aphasia, cognitive-communication disorders and swallow evaluation — delivered by Kathleen McGrath, M.S. CCC-SLP, licensed to practise in New Hampshire (ASHA CCC-SLP).",
    cities: [
      "Manchester",
      "Nashua",
      "Concord",
      "Portsmouth",
      "Dover",
      "Rochester",
      "Keene",
      "Lebanon",
      "Hanover",
      "Salem",
      "Derry",
      "Laconia",
    ],
    phone: "+1 (518) 795-5154",
    tel: "+15187955154",
    modalityNote:
      "Sessions are delivered via secure, HIPAA-compliant telehealth on Zoom for Healthcare — with a Business Associate Agreement (BAA) in place. This means you can access specialist adult neurological SLP care from anywhere in the state, without a commute.",
    price: "USD",
    title:
      "Speech Therapy in New Hampshire · Adult SLP · Parkinson's, Stroke, Swallow",
    description:
      "New Hampshire adult speech-language pathologist (SLP). Kathleen McGrath, M.S. CCC-SLP — Parkinson's SPEAK OUT!®, post-stroke aphasia, cognitive-communication therapy and adult swallow evaluation. Secure HIPAA-compliant telehealth.",
    path: "/new-hampshire",
    canonical: "https://neurocompartners.com/new-hampshire",
    addressLocality: "Manchester",
    addressRegion: "NH",
    ianaCurrency: "USD",
    languageLabel: "American English",
  },
  vermont: {
    code: "VT",
    country: "US",
    region: "Vermont",
    regionShort: "VT",
    headline: "Adult Speech-Language Pathologist serving Vermont.",
    eyebrow: "Vermont · Licensed SLP",
    intro:
      "Specialist adult SLP care for Vermonters living with Parkinson's disease, stroke recovery and cognitive change. Licensed in Vermont (ASHA CCC-SLP) and delivered by Kathleen McGrath, M.S. CCC-SLP — via secure telehealth statewide.",
    cities: [
      "Burlington",
      "South Burlington",
      "Colchester",
      "Essex",
      "Rutland",
      "Montpelier",
      "Barre",
      "Brattleboro",
      "Middlebury",
      "St. Albans",
      "White River Junction",
      "Bennington",
    ],
    phone: "+1 (518) 795-5154",
    tel: "+15187955154",
    modalityNote:
      "Vermont's rural geography and long winters can make in-person appointments hard. Every session is delivered via secure, HIPAA-compliant Zoom for Healthcare telehealth — with a BAA — so you can meet from home, without driving.",
    price: "USD",
    title:
      "Speech Therapy in Vermont · Adult SLP · Parkinson's, Stroke, Swallow",
    description:
      "Vermont adult speech-language pathologist (SLP). Kathleen McGrath, M.S. CCC-SLP — Parkinson's SPEAK OUT!®, post-stroke aphasia, cognitive-communication therapy and adult swallow evaluation, statewide via HIPAA-compliant telehealth.",
    path: "/vermont",
    canonical: "https://neurocompartners.com/vermont",
    addressLocality: "Burlington",
    addressRegion: "VT",
    ianaCurrency: "USD",
    languageLabel: "American English",
  },
  ireland: {
    code: "IE",
    country: "IE",
    region: "Ireland",
    regionShort: "IE",
    headline:
      "Adult Speech & Language Therapist serving clients across Ireland.",
    eyebrow: "Ireland · CORU-registered SLT",
    intro:
      "CORU-registered Speech & Language Therapy for adults living with Parkinson's disease, post-stroke communication difficulties, cognitive change and swallowing difficulties — delivered by Kathleen McGrath, M.S. CCC-SLP, via secure GDPR-compliant telehealth.",
    cities: [
      "Dublin",
      "Cork",
      "Limerick",
      "Galway",
      "Waterford",
      "Kilkenny",
      "Sligo",
      "Wexford",
      "Drogheda",
      "Dundalk",
      "Athlone",
      "Ennis",
    ],
    phone: "+353 87 377 3620",
    tel: "+353873773620",
    modalityNote:
      "Sessions are delivered via secure, GDPR-compliant telehealth on Zoom for Healthcare, so you can access private adult neurological SLT care from anywhere in Ireland — Dublin, Cork, Galway, or rural areas typically underserved for private neurological rehab.",
    price: "EUR",
    title:
      "Speech & Language Therapy in Ireland · Adult SLT · Parkinson's, Stroke, Swallow",
    description:
      "Ireland adult speech & language therapist (SLT). Kathleen McGrath, M.S. CCC-SLP, CORU-registered — Parkinson's SPEAK OUT!®, post-stroke aphasia, cognitive-communication therapy and adult swallow evaluation. GDPR-compliant telehealth nationwide.",
    path: "/ireland",
    canonical: "https://neurocompartners.com/ireland",
    addressLocality: "Dublin",
    addressRegion: null,
    ianaCurrency: "EUR",
    languageLabel: "English (Ireland)",
  },
};

const SERVICES = [
  {
    name: "Parkinson's Voice Therapy · SPEAK OUT!®",
    body: "Evidence-based voice preservation programme for people with Parkinson's disease. 12 focused sessions to keep your voice loud, clear and confident.",
    href: "/#treatments",
  },
  {
    name: "Post-Stroke Aphasia & Cognitive-Communication Therapy",
    body: "Rebuild language, word-finding, reading, writing and conversation after a CVA — with functional, life-focused goals.",
    href: "/#specialties",
  },
  {
    name: "Cognitive-Communication Therapy",
    body: "For adults living with dementia, mild cognitive impairment, TBI or chronic illness — strategies that support memory, attention and daily independence.",
    href: "/#specialties",
  },
  {
    name: "Adult Swallow (Dysphagia) Evaluation",
    body: "Clinical evaluation of swallowing difficulty (dysphagia) with a plan for safe, comfortable eating and drinking. Referral for instrumental studies (FEES / VFSS) when clinically indicated.",
    href: "/#treatments",
  },
];

export default function LocationPage() {
  const { pathname } = useLocation();
  const routeParam = pathname.replace(/^\//, "").split("/")[0];
  const region = REGIONS[routeParam];
  const [open, setOpen] = useState(false);

  usePageMeta({
    title: region?.title,
    description: region?.description,
    path: region?.path,
  });

  const jsonLd = useMemo(() => {
    if (!region) return null;
    return [
      {
        "@context": "https://schema.org",
        "@type": ["MedicalBusiness", "LocalBusiness"],
        "@id": `${region.canonical}#business`,
        name: `Neurological Communication Partners — ${region.region}`,
        alternateName: [
          "Neurocompartners",
          `Adult SLP ${region.region}`,
          `Speech Therapy ${region.region}`,
        ],
        description: region.description,
        url: region.canonical,
        image:
          "https://customer-assets.emergentagent.com/job_swallow-eval-center/artifacts/z294o117_FullLogo.jpg",
        logo:
          "https://customer-assets.emergentagent.com/job_swallow-eval-center/artifacts/z294o117_FullLogo.jpg",
        telephone: region.phone,
        email: "neurocompartners@gmail.com",
        priceRange: "$$",
        currenciesAccepted: region.ianaCurrency,
        paymentAccepted: "Credit Card, Debit Card, Bank Transfer",
        address: {
          "@type": "PostalAddress",
          addressLocality: region.addressLocality,
          ...(region.addressRegion
            ? { addressRegion: region.addressRegion }
            : {}),
          addressCountry: region.country,
        },
        areaServed: {
          "@type": region.country === "US" ? "State" : "Country",
          name: region.region,
        },
        availableLanguage: [region.languageLabel],
        medicalSpecialty: [
          "SpeechPathology",
          "Rehabilitation",
          "Geriatric",
          "Neurology",
        ],
        provider: {
          "@type": "Physician",
          "@id": "https://neurocompartners.com/#kathleen",
          name: "Kathleen McGrath",
          jobTitle: "Speech-Language Pathologist, M.S. CCC-SLP",
          medicalSpecialty: "SpeechPathology",
          hasCredential: [
            {
              "@type": "EducationalOccupationalCredential",
              credentialCategory: "certification",
              name: "ASHA Certificate of Clinical Competence (CCC-SLP)",
            },
            {
              "@type": "EducationalOccupationalCredential",
              credentialCategory: "license",
              name: "CORU Registered Speech & Language Therapist (Ireland)",
            },
          ],
        },
        makesOffer: SERVICES.map((s) => ({
          "@type": "Offer",
          itemOffered: {
            "@type": "MedicalTherapy",
            name: s.name,
            description: s.body,
          },
        })),
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
            ],
            opens: "09:00",
            closes: "18:00",
          },
        ],
        sameAs: [
          "https://instagram.com/Neurocompartners",
          "https://neurocompartners.com",
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://neurocompartners.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: region.region,
            item: region.canonical,
          },
        ],
      },
    ];
  }, [region]);

  useJsonLd(jsonLd, `location-jsonld-${routeParam || "unknown"}`);

  if (!region) {
    return (
      <main className="min-h-screen bg-[#FDFCF8] text-[#2D332F] flex items-center justify-center p-8">
        <div className="text-center max-w-md">
          <p className="text-xs uppercase tracking-[0.25em] text-[#5C6661] mb-3">
            Location not found
          </p>
          <h1 className="font-serif text-3xl md:text-4xl font-light mb-6">
            We don&rsquo;t serve that region yet.
          </h1>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2D332F] text-[#FDFCF8] text-sm"
          >
            Return home
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main
      data-testid={`location-page-${routeParam}`}
      className="bg-[#FDFCF8] text-[#2D332F]"
    >
      <Navbar onBookClick={() => setOpen(true)} />

      {/* Hero */}
      <section className="relative overflow-hidden pt-36 md:pt-44 pb-16 md:pb-24">
        <div
          className="orb orb-sage"
          style={{ width: 520, height: 520, top: -120, left: -160 }}
        />
        <div
          className="orb orb-sand"
          style={{ width: 380, height: 380, bottom: -140, right: -120 }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.25em] text-[#5C6661] mb-8 flex items-center gap-3"
            data-testid="location-eyebrow"
          >
            <span className="w-8 h-px bg-[#739072]" />
            {region.eyebrow}
          </motion.p>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-[#2D332F]"
                data-testid="location-title"
              >
                {region.headline
                  .split(" ")
                  .slice(0, -1)
                  .join(" ")}{" "}
                <em className="text-[#739072]">
                  {region.headline.split(" ").slice(-1)[0]}
                </em>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.25 }}
                className="mt-8 text-lg md:text-xl text-[#5C6661] leading-relaxed max-w-2xl"
              >
                {region.intro}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="mt-10 flex flex-col sm:flex-row gap-4"
              >
                <button
                  onClick={() => setOpen(true)}
                  data-testid="location-cta-book"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#2D332F] text-[#FDFCF8] text-sm tracking-wide hover:bg-[#739072] transition-all duration-300 group"
                >
                  Book a Consult
                  <ArrowUpRight
                    size={16}
                    strokeWidth={1.5}
                    className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                  />
                </button>
                <a
                  href={`tel:${region.tel}`}
                  data-testid="location-cta-phone"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-[#2D332F]/20 text-[#2D332F] text-sm tracking-wide hover:bg-[#2D332F] hover:text-[#FDFCF8] transition-all duration-300"
                >
                  <Phone size={14} strokeWidth={1.5} />
                  {region.phone}
                </a>
              </motion.div>
            </div>

            <motion.aside
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.5 }}
              className="lg:col-span-4"
            >
              <div className="bg-white/70 backdrop-blur-xl border border-white/40 rounded-3xl p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <div className="flex items-center gap-2 text-[#5C6661] text-xs uppercase tracking-[0.25em] mb-5">
                  <Video size={14} strokeWidth={1.5} />
                  How it works
                </div>
                <p className="text-sm text-[#2D332F] leading-relaxed">
                  {region.modalityNote}
                </p>
                <div className="divider-line my-5" />
                <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661] mb-2">
                  Credentials
                </p>
                <p className="text-sm text-[#2D332F]">
                  {region.country === "IE"
                    ? "CORU-registered Speech & Language Therapist · M.S. CCC-SLP"
                    : `Licensed SLP · ${region.region} · ASHA CCC-SLP`}
                </p>
              </div>
            </motion.aside>
          </div>
        </div>
      </section>

      {/* Services */}
      <section
        data-testid="location-services"
        className="relative py-20 md:py-28"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#5C6661] mb-4">
                Adult SLP Services · {region.region}
              </p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#2D332F] max-w-3xl leading-tight">
                Specialist adult neurological
                <br />
                <em className="text-[#739072]">speech therapy.</em>
              </h2>
            </div>
            <p className="text-[#5C6661] max-w-sm leading-relaxed">
              Every plan is built around your goals, your family, and the daily
              function that matters most.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {SERVICES.map((s, i) => (
              <motion.a
                key={s.name}
                href={s.href}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                data-testid={`location-service-${i}`}
                className="group block rounded-3xl border border-black/5 bg-[#FDFCF8] p-8 md:p-10 hover:-translate-y-1 hover:shadow-lg transition-all duration-500"
              >
                <div className="flex items-start justify-between mb-4">
                  <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661]">
                    0{i + 1} — Service
                  </p>
                  <ArrowUpRight
                    size={18}
                    strokeWidth={1.5}
                    className="text-[#2D332F]/40 group-hover:text-[#739072] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                  />
                </div>
                <h3 className="font-serif text-xl md:text-2xl font-light text-[#2D332F] mb-3 leading-tight">
                  {s.name}
                </h3>
                <p className="text-[#5C6661] leading-relaxed">{s.body}</p>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Cities */}
      <section
        data-testid="location-cities"
        className="relative py-20 md:py-28 bg-[#F3F1E7]/40"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="text-xs uppercase tracking-[0.25em] text-[#5C6661] mb-4 flex items-center gap-3">
                <MapPin size={14} strokeWidth={1.5} />
                Cities & towns served
              </p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#2D332F] leading-tight">
                Adult speech therapy,
                <br />
                <em className="text-[#739072]">
                  wherever you are in {region.region}.
                </em>
              </h2>
              <p className="mt-6 text-[#5C6661] leading-relaxed max-w-md">
                Because sessions run on secure telehealth, there is no commute
                and no waiting room — private specialist SLP care from your
                living room.
              </p>
            </div>

            <div className="lg:col-span-7">
              <motion.ul
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="grid grid-cols-2 md:grid-cols-3 gap-3"
              >
                {region.cities.map((city) => (
                  <li
                    key={city}
                    data-testid={`city-${city.toLowerCase().replace(/[.\s]/g, "-")}`}
                    className="flex items-center gap-2 rounded-2xl border border-black/5 bg-[#FDFCF8] px-4 py-3"
                  >
                    <Check
                      size={14}
                      strokeWidth={2}
                      className="text-[#739072] shrink-0"
                    />
                    <span className="text-sm text-[#2D332F]">{city}</span>
                  </li>
                ))}
              </motion.ul>
              <p className="mt-6 text-xs text-[#5C6661]">
                Don&rsquo;t see your town?{" "}
                <button
                  onClick={() => setOpen(true)}
                  className="underline text-[#739072] hover:text-[#5C765B]"
                >
                  Get in touch
                </button>{" "}
                — telehealth covers the entire {region.region} region.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-[#5C6661] mb-4">
            Get started
          </p>
          <h2 className="font-serif text-3xl md:text-5xl font-light tracking-tight text-[#2D332F] leading-[1.05]">
            Ready to speak with{" "}
            <em className="text-[#739072]">Kathleen?</em>
          </h2>
          <p className="mt-6 text-[#5C6661] text-lg leading-relaxed max-w-2xl mx-auto">
            Book a free 15-minute discovery call. We&rsquo;ll talk through your
            goals, answer any questions, and — if it&rsquo;s a good fit —
            schedule your first session.
          </p>
          <button
            onClick={() => setOpen(true)}
            data-testid="location-cta-book-bottom"
            className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#2D332F] text-[#FDFCF8] text-sm tracking-wide hover:bg-[#739072] transition-all duration-300 group"
          >
            Request an appointment
            <ArrowUpRight
              size={16}
              strokeWidth={1.5}
              className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
            />
          </button>
        </div>
      </section>

      <Footer />
      <AppointmentDialog
        open={open}
        onOpenChange={setOpen}
        initialRegion={region.regionShort}
      />
      <CookieBanner />
    </main>
  );
}
